import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { UploadBox } from "./UploadBox";
import { FieldInput } from "./FieldInput";
import { FieldEditor, cleanFieldsForSave } from "./FieldEditor";

const ReviewTable = ({ fields, rows, setRows }) => {
  const set = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="max-h-[50vh] overflow-auto rounded-lg border border-slate-200" data-testid="import-review-table">
      <table className="text-xs w-full">
        <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600">
          <tr><th className="p-2 text-left">Import</th>{fields.map((f) => <th key={f.key} className="p-2 text-left whitespace-nowrap">{f.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={`border-t border-slate-100 ${r.duplicate ? "bg-amber-50" : ""} ${r.include ? "" : "opacity-50"}`} data-testid={`import-row-${i}`}>
              <td className="p-2 whitespace-nowrap">
                <input type="checkbox" checked={r.include} onChange={(e) => set(i, { include: e.target.checked })} data-testid={`import-include-${i}`} />
                {r.duplicate && <span className="ml-1.5 inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid={`import-duplicate-${i}`}>
                  <AlertTriangle className="w-3 h-3" />{r.duplicate === "existing" ? "Already in table" : "Repeated"}</span>}
              </td>
              {fields.map((f) => (
                <td key={f.key} className={`p-1 ${f.required && !r.data[f.key] ? "bg-rose-50" : ""}`}>
                  <FieldInput compact field={f} value={r.data[f.key]} onChange={(v) => set(i, { data: { ...r.data, [f.key]: v } })} testid={`import-cell-${i}-${f.key}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const ImportDialog = ({ open, onClose, template, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [step, setStep] = useState("upload");
  useEffect(() => { if (open) { setRes(null); setStep("upload"); } }, [open]);
  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = (await api.post(`/forms/templates/${template.template_id}/extract-rows`, fd, { timeout: 300000 })).data;
      setRes({ ...r, rows: r.rows.map((x) => ({ ...x, include: !x.duplicate })) });
      setStep(r.fields_proposed ? "fields" : "review");
      if (!r.rows.length) toast.warning("No entries were found in this file");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doImport = async () => {
    setBusy(true);
    try {
      const fields = res.fields_proposed ? cleanFieldsForSave(res.fields) : undefined;
      const r = await api.post(`/forms/templates/${template.template_id}/import`, {
        rows: res.rows.filter((x) => x.include).map((x) => ({ data: x.data })), fields, source_upload_id: res.upload.upload_id });
      toast.success(`${r.data.imported} entries imported`); onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const n = res?.rows.filter((x) => x.include).length || 0;
  const dups = res?.rows.filter((x) => x.duplicate).length || 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600" />Import current records · {template.name}</DialogTitle>
          <DialogDescription>AI reads the file and proposes entries. Nothing is saved until you review and press Import.</DialogDescription>
        </DialogHeader>
        {step === "upload" && <UploadBox onFile={upload} busy={busy} title="Upload filled records" testid="import-upload" />}
        {step === "fields" && (
          <div className="space-y-3" data-testid="import-fields-step">
            <p className="text-sm text-slate-600">Step 1 of 2 — confirm the fields found in <b>{res.upload.filename}</b>. Rename, reorder or change types before importing.</p>
            <FieldEditor fields={res.fields} onChange={(fields) => setRes({ ...res, fields })} />
          </div>
        )}
        {step === "review" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 text-sm" data-testid="import-summary">
              <span className="text-slate-700">{res.rows.length} entries found in <b>{res.upload.filename}</b></span>
              {dups > 0 && <span className="text-amber-700 font-semibold">{dups} possible duplicates (unticked)</span>}
              <span className="text-slate-500">Red cells are required but empty.</span>
            </div>
            <ReviewTable fields={res.fields_proposed ? cleanFieldsForSave(res.fields) : res.fields} rows={res.rows} setRows={(rows) => setRes({ ...res, rows })} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose} data-testid="import-cancel">Cancel</Button>
          {step === "fields" && <Button onClick={() => setStep("review")} disabled={!cleanFieldsForSave(res.fields).length} data-testid="import-fields-next">Next: review entries</Button>}
          {step === "review" && <Button onClick={doImport} disabled={busy || n === 0} data-testid="import-confirm">Import {n} entries</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
